import React, { useState } from 'react';
import { useEnvironment } from '../contexts/EnvironmentContext';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose }) => {
  const { apiBaseUrl } = useEnvironment();
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState('general');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categories = [
    { value: 'general', label: 'General Feedback' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'ui', label: 'UI/UX Issue' },
    { value: 'performance', label: 'Performance Issue' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback: feedback.trim(),
          rating,
          category,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
          senderEmail: senderEmail.trim() || null,
          senderName: senderName.trim() || null
        }),
      });

      if (response.ok) {
        await response.json();
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          setFeedback('');
          setRating(0);
          setCategory('general');
          setSenderEmail('');
          setSenderName('');
        }, 3000);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setFeedback('');
      setRating(0);
      setCategory('general');
      setSenderEmail('');
      setSenderName('');
      setSubmitted(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2>💬 Beta Feedback</h2>
          <button 
            className="feedback-close-btn" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {submitted ? (
          <div className="feedback-success">
          <div className="success-icon">✅</div>
          <h3>Thank you for your feedback!</h3>
          <p>Your feedback has been sent to our team and helps us improve the product.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            <div className="feedback-section">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="feedback-select"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="feedback-section">
              <label>Rating</label>
              <div className="rating-container">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`rating-star ${star <= rating ? 'active' : ''}`}
                    onClick={() => setRating(star)}
                    disabled={isSubmitting}
                  >
                    ⭐
                  </button>
                ))}
                <span className="rating-text">
                  {rating === 0 ? 'Select rating' : 
                   rating === 1 ? 'Poor' :
                   rating === 2 ? 'Fair' :
                   rating === 3 ? 'Good' :
                   rating === 4 ? 'Very Good' : 'Excellent'}
                </span>
              </div>
            </div>

            <div className="feedback-section">
              <label htmlFor="senderEmail">Your Email (Optional)</label>
              <input
                type="email"
                id="senderEmail"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="feedback-input"
                disabled={isSubmitting}
              />
              <small className="feedback-help-text">
                If provided, feedback emails will be sent from this address for better deliverability
              </small>
            </div>

            <div className="feedback-section">
              <label htmlFor="senderName">Your Name (Optional)</label>
              <input
                type="text"
                id="senderName"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="Your Name"
                className="feedback-input"
                disabled={isSubmitting}
              />
            </div>

            <div className="feedback-section">
              <label htmlFor="feedback">Your Feedback</label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell us what you think! What's working well? What could be improved? Any bugs you've encountered?"
                className="feedback-textarea"
                rows={6}
                maxLength={2000}
                disabled={isSubmitting}
                required
              />
              <div className="character-count">
                {feedback.length}/2000 characters
              </div>
            </div>

            <div className="feedback-actions">
              <button
                type="button"
                onClick={handleClose}
                className="feedback-cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="feedback-submit-btn"
                disabled={!feedback.trim() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
